// frontend/src/components/ui/VerificationCodeInput.js
import React, { useState, useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';
import api from '../../utils/api';

const VerificationCodeInput = ({ 
  phoneNumber, 
  onVerificationChange, 
  verificationType = 'register',
  disabled = false 
}) => {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    // Validate phone number
    if (!phoneNumber || phoneNumber.length < 10) {
      setMessage('请输入有效的手机号码');
      setMessageType('error');
      return;
    }

    setSending(true);
    setMessage('');
    
    try {
      // Send request to the appropriate endpoint based on verification type
      let endpoint = '/sms/send-verification';
      let requestData = { phone_number: phoneNumber };
      
      if (verificationType === 'password_reset') {
        endpoint = '/sms/send-reset-code';
      } else if (verificationType === 'phone_change') {
        endpoint = '/sms/send-phone-change-code';
        requestData = { new_phone_number: phoneNumber };
      }
      
      const response = await api.post(endpoint, requestData);
      
      if (response.data.success) {
        setMessage(response.data.message);
        setMessageType('success');
        setCountdown(60); // Start 60 second countdown
      } else {
        setMessage(response.data.message);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      
      // Handle specific error messages from the backend
      let errorMessage = '发送验证码失败，请稍后再试';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.success === false) {
        errorMessage = error.response.data.message || errorMessage;
      }
      
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setSending(false);
    }
  };

  const handleCodeChange = (e) => {
    // Only allow numbers and limit to 6 digits
    const newCode = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(newCode);
    
    // Pass the code up to parent component
    onVerificationChange(newCode);
  };

  // Get button text based on verification type
  const getButtonText = () => {
    if (sending) return <FaSpinner className="animate-spin" />;
    if (countdown > 0) return `${countdown}s`;
    
    switch (verificationType) {
      case 'password_reset':
        return '获取重置码';
      case 'phone_change':
        return '获取更换码';
      default:
        return '获取验证码';
    }
  };

  // Get placeholder text based on verification type
  const getPlaceholderText = () => {
    switch (verificationType) {
      case 'password_reset':
        return '请输入6位重置码';
      case 'phone_change':
        return '请输入6位更换码';
      default:
        return '请输入6位验证码';
    }
  };

  return (
    <div className="verification-code-input">
      <div className="flex items-center mb-1">
        <label className="block text-gray-700">
          {verificationType === 'password_reset' ? '重置码' : 
           verificationType === 'phone_change' ? '更换码' : '验证码'}
        </label>
        {message && (
          <span className={`ml-2 text-xs ${messageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {message}
          </span>
        )}
      </div>
      
      <div className="flex">
        <input
          type="text"
          value={code}
          onChange={handleCodeChange}
          placeholder={getPlaceholderText()}
          className="flex-1 p-2 border rounded-l"
          maxLength={6}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={handleSendCode}
          className={`px-4 py-2 rounded-r whitespace-nowrap ${
            countdown > 0 || sending || disabled
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-red-700 text-white hover:bg-red-800'
          }`}
          disabled={countdown > 0 || sending || disabled}
        >
          {getButtonText()}
        </button>
      </div>
      
      {/* Additional info for user guidance */}
      {messageType === 'error' && message.includes('已存在') && verificationType === 'register' && (
        <div className="mt-2 text-sm text-blue-600">
          如果您已有账号，请直接 
          <a href="/login" className="underline hover:text-blue-800">登录</a>
        </div>
      )}
    </div>
  );
};

export default VerificationCodeInput;